import { Decimal, toDecimal } from '../money/decimal.js';
import { money, type Money } from '../money/money.js';
import { addToBag, bagEntries, emptyBag, type MoneyBag } from '../money/bag.js';
import { assertNever, type Currency } from '../types/enums.js';
import type { InvestmentTransaction, IsoDate, Uuid } from '../types/domain.js';

/**
 * A holding, derived from the transaction ledger.
 *
 * Nothing about a position is stored: re-running this over the ledger is the
 * single source of truth, which is what makes a re-import or a corrected trade
 * self-healing.
 */
export interface Position {
  accountId: Uuid;
  instrumentId: Uuid;
  /** Signed — a short position is negative. */
  quantity: Decimal;
  /** Currency the instrument trades in, taken from its first transaction. */
  currency: Currency;
  /** Remaining book cost of the open quantity. */
  costBasis: Money;
  /** costBasis / quantity, or null while the position is flat. */
  averageCost: Decimal | null;
  realizedPnl: Money;
  fees: MoneyBag;
  taxes: MoneyBag;
  /** Dividends and interest. */
  income: MoneyBag;
  lastTradeDate: IsoDate | null;
}

export function positionKey(accountId: Uuid, instrumentId: Uuid): string {
  return `${accountId}:${instrumentId}`;
}

interface MutablePosition {
  accountId: Uuid;
  instrumentId: Uuid;
  quantity: Decimal;
  currency: Currency;
  costBasis: Decimal;
  realizedPnl: Decimal;
  fees: Map<Currency, Decimal>;
  taxes: Map<Currency, Decimal>;
  income: Map<Currency, Decimal>;
  lastTradeDate: IsoDate | null;
}

export class PositionCurrencyError extends Error {
  constructor(instrumentId: Uuid, expected: Currency, actual: Currency) {
    super(
      `Instrument ${instrumentId} trades in ${expected} but a transaction arrived in ${actual}`,
    );
    this.name = 'PositionCurrencyError';
  }
}

/**
 * Fold a transaction ledger into positions.
 *
 * Cost basis uses a moving average rather than FIFO lots. v1 has no tax
 * reporting (an explicit non-goal), and moving average survives the partial
 * history that comes out of the sheet import, where lot-level detail is gone.
 */
export function buildPositions(transactions: readonly InvestmentTransaction[]): Position[] {
  const positions = new Map<string, MutablePosition>();

  for (const tx of sortByTradeDate(transactions)) {
    // Cash-only rows (account-level fees, FX) carry no instrument and are not a holding.
    if (tx.instrumentId === null) continue;

    const key = positionKey(tx.accountId, tx.instrumentId);
    let position = positions.get(key);
    if (!position) {
      position = {
        accountId: tx.accountId,
        instrumentId: tx.instrumentId,
        quantity: new Decimal(0),
        currency: tx.currency,
        costBasis: new Decimal(0),
        realizedPnl: new Decimal(0),
        fees: emptyBag(),
        taxes: emptyBag(),
        income: emptyBag(),
        lastTradeDate: null,
      };
      positions.set(key, position);
    }

    applyTransaction(position, tx);
    position.lastTradeDate =
      position.lastTradeDate === null || tx.tradeDate > position.lastTradeDate
        ? tx.tradeDate
        : position.lastTradeDate;
  }

  return [...positions.values()].map(freezePosition);
}

function applyTransaction(position: MutablePosition, tx: InvestmentTransaction): void {
  // `amount` is the signed cash effect on the account; direction comes from `type`.
  const cash = toDecimal(tx.amount).abs();
  const quantity = tx.quantity === null ? new Decimal(0) : toDecimal(tx.quantity).abs();

  switch (tx.type) {
    case 'buy': {
      requireCurrency(position, tx);
      position.quantity = position.quantity.plus(quantity);
      position.costBasis = position.costBasis.plus(cash);
      return;
    }
    case 'sell': {
      requireCurrency(position, tx);
      const averageCost = position.quantity.isZero()
        ? new Decimal(0)
        : position.costBasis.dividedBy(position.quantity);
      const relievedCost = averageCost.times(quantity);
      position.realizedPnl = position.realizedPnl.plus(cash.minus(relievedCost));
      position.quantity = position.quantity.minus(quantity);
      position.costBasis = position.quantity.isZero()
        ? new Decimal(0)
        : position.costBasis.minus(relievedCost);
      return;
    }
    case 'fee':
      addToBag(position.fees, money(cash, tx.currency));
      return;
    case 'tax':
      addToBag(position.taxes, money(cash, tx.currency));
      return;
    case 'dividend':
    case 'interest':
      addToBag(position.income, money(cash, tx.currency));
      return;
    default:
      return assertNever(tx.type, 'Unhandled investment transaction type');
  }
}

function requireCurrency(position: MutablePosition, tx: InvestmentTransaction): void {
  if (position.currency !== tx.currency) {
    throw new PositionCurrencyError(position.instrumentId, position.currency, tx.currency);
  }
}

/** Trade date first, then id, so two runs over the same ledger produce identical output. */
function sortByTradeDate(transactions: readonly InvestmentTransaction[]): InvestmentTransaction[] {
  return [...transactions].sort((a, b) => {
    if (a.tradeDate !== b.tradeDate) return a.tradeDate < b.tradeDate ? -1 : 1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

function freezePosition(position: MutablePosition): Position {
  return {
    accountId: position.accountId,
    instrumentId: position.instrumentId,
    quantity: position.quantity,
    currency: position.currency,
    costBasis: money(position.costBasis, position.currency),
    averageCost: position.quantity.isZero()
      ? null
      : position.costBasis.dividedBy(position.quantity),
    realizedPnl: money(position.realizedPnl, position.currency),
    fees: position.fees,
    taxes: position.taxes,
    income: position.income,
    lastTradeDate: position.lastTradeDate,
  };
}

/** Positions that still hold something. Closed ones stay out of the dashboard. */
export function openPositions(positions: readonly Position[]): Position[] {
  return positions.filter((position) => !position.quantity.isZero());
}

export function marketValue(position: Position, price: Money): Money {
  if (price.currency !== position.currency) {
    throw new PositionCurrencyError(position.instrumentId, position.currency, price.currency);
  }
  return money(position.quantity.times(price.amount), price.currency);
}

export function unrealizedPnl(position: Position, price: Money): Money {
  const value = marketValue(position, price);
  return money(value.amount.minus(position.costBasis.amount), position.currency);
}

/** Flatten every fee/tax/income bag on a position into a list of amounts. */
export function positionCashflows(position: Position): {
  fees: Money[];
  taxes: Money[];
  income: Money[];
} {
  return {
    fees: bagEntries(position.fees),
    taxes: bagEntries(position.taxes),
    income: bagEntries(position.income),
  };
}
